import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeSlash, Lock, User, ShieldCheck, ChartLineUp, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { seedAPI } from '@/lib/api';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [loginError, setLoginError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!username || !password) {
      setLoginError('Please enter username and password');
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login successful');
      navigate('/');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid credentials';
      setLoginError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupAdmin = async () => {
    setSeeding(true);
    try {
      const response = await seedAPI.createAdmin();
      toast.success(`Admin created: ${response.data.username} / ${response.data.password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]" data-testid="login-page">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden bg-[#0B1B3D]">
        {/* subtle grid texture */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(#2E7BC4 1px, transparent 1px), linear-gradient(90deg, #2E7BC4 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#0F4C81]/40 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-[#2E7BC4]/20 blur-3xl" />

        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 w-full">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-2.5 py-1.5 flex items-center shadow-sm">
              <img
                src="/fintexa-icon.png"
                alt="Fintexa"
                className="h-7 w-auto"
              />
            </div>
            <span className="text-xl font-bold text-white tracking-tight font-['Plus_Jakarta_Sans']">Fintexa</span>
          </div>

          <div className="max-w-md">
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-[#7FB2E5] mb-4">
              Admin Console
            </span>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight font-['Plus_Jakarta_Sans']">
              Enterprise Finance,<br />HR &amp; Chain Management
            </h1>
            <p className="text-base text-[#94A3B8] mt-5 leading-relaxed">
              One trusted platform for accounting, compliance, inventory and payroll across every HearClear clinic.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: ChartLineUp, text: 'Real-time revenue &amp; GST intelligence' },
                { icon: Buildings, text: 'Multi-clinic billing &amp; inventory control' },
                { icon: ShieldCheck, text: 'Role-based access &amp; full audit trail' },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#7FB2E5]" weight="bold" />
                    </div>
                    <span className="text-sm text-[#CBD5E1]" dangerouslySetInnerHTML={{ __html: f.text }} />
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-[#64748B]">
            © {new Date().getFullYear()} Fintexa ERP · HearClear Clinic Operations Platform
          </p>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <img
              src="/fintexa-icon.png"
              alt="Fintexa"
              className="h-9 w-auto"
            />
            <span className="text-xl font-bold text-[#0B1B3D] font-['Plus_Jakarta_Sans']">Fintexa</span>
          </div>

          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EBF3FA] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#0F4C81]">
              <ShieldCheck size={13} weight="bold" /> Admin Portal
            </span>
            <h2 className="text-2xl font-bold text-[#0F172A] mt-4 tracking-tight font-['Plus_Jakarta_Sans']">
              Sign in to Fintexa
            </h2>
            <p className="text-sm text-[#64748B] mt-1.5">
              Enter your credentials to access the finance dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {loginError && (
              <div
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
                data-testid="login-error"
                role="alert"
              >
                <Lock size={16} className="mt-0.5 shrink-0" />
                <span>{loginError}</span>
              </div>
            )}
            <div>
              <label className="form-label">Username</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input pl-10"
                  placeholder="Enter your username"
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-10 pr-10"
                  placeholder="Enter your password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F4C81]"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
            <p className="text-xs text-[#64748B] text-center mb-3">First time setup?</p>
            <button
              onClick={handleSetupAdmin}
              disabled={seeding}
              className="btn-ghost w-full text-sm"
              data-testid="setup-admin-btn"
            >
              {seeding ? 'Setting up...' : 'Create Admin Account'}
            </button>
          </div>

          <p className="text-center text-xs text-[#64748B] mt-8">
            Secured with role-based access control
          </p>
        </div>
      </div>
    </div>
  );
}
